import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SIDE_ARROW_CLASS =
  'group hidden sm:flex absolute top-1/2 -translate-y-1/2 z-10 h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-full bg-runcards-accent text-white shadow-lg shadow-runcards-accent/25 ring-1 ring-inset ring-white/10 transition-all duration-300 ease-out hover:bg-runcards-accent-hover hover:scale-105 hover:shadow-xl hover:shadow-runcards-accent/30 active:scale-95';

/**
 * Previous/Next arrows positioned on left and right of carousel.
 */
export const CarouselSideArrows = ({ onPrev, onNext, ariaLabelPrefix }) => (
  <>
    <button
      type="button"
      onClick={onPrev}
      className={`left-0 ${SIDE_ARROW_CLASS}`}
      aria-label={`Previous ${ariaLabelPrefix}`}
    >
      <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 transition-transform duration-300 ease-out group-hover:-translate-x-0.5" />
    </button>
    <button
      type="button"
      onClick={onNext}
      className={`right-0 ${SIDE_ARROW_CLASS}`}
      aria-label={`Next ${ariaLabelPrefix}`}
    >
      <ChevronRight className="h-5 w-5 md:h-6 md:w-6 transition-transform duration-300 ease-out group-hover:translate-x-0.5" />
    </button>
  </>
);

/** Number of cards visible on desktop (used for max index and dot count) */
export const CAROUSEL_CARDS_DESKTOP = 4;

/** Percentage width per card on desktop (100 / cards per view) */
export const CAROUSEL_CARD_WIDTH_PCT = 100 / CAROUSEL_CARDS_DESKTOP;

/**
 * @param {number} itemCount - Total number of items
 * @param {boolean} isMobile - Whether mobile breakpoint
 * @returns {number} Max valid carousel index (0-based)
 */
export function getCarouselMaxIndex(itemCount, isMobile) {
  if (!itemCount) return 0;
  const cardsPerView = isMobile ? 1 : CAROUSEL_CARDS_DESKTOP;
  return Math.max(0, itemCount - cardsPerView);
}

/**
 * @param {number} itemCount - Total number of items
 * @param {boolean} isMobile - Whether mobile breakpoint
 * @returns {number} Number of pagination dots to show
 */
export function getCarouselDotCount(itemCount, isMobile) {
  if (!itemCount) return 1;
  if (isMobile) return itemCount;
  return Math.max(1, itemCount - CAROUSEL_CARDS_DESKTOP + 1);
}

/** Don't show dots when there are more than this many (avoids a long strip); arrows still work */
const MAX_DOTS_VISIBLE = 6;

const NAV_BUTTON_CLASS =
  'group flex h-11 w-11 items-center justify-center rounded-full bg-runcards-accent text-white shadow-md shadow-runcards-accent/25 ring-1 ring-inset ring-white/10 transition-all duration-300 ease-out hover:bg-runcards-accent-hover hover:shadow-lg active:scale-95';

/**
 * Reusable carousel navigation: pagination dots + prev/next arrows.
 * Uses black arrows on white buttons for visibility.
 */
const CarouselNav = ({
  currentIndex,
  itemCount,
  onIndexChange,
  isMobile,
  ariaLabelPrefix = 'Slide',
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}) => {
  const maxIndex = getCarouselMaxIndex(itemCount, isMobile);
  const dotCount = getCarouselDotCount(itemCount, isMobile);

  const goPrev = () => {
    onIndexChange((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goNext = () => {
    onIndexChange((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const showDots = dotCount <= MAX_DOTS_VISIBLE;
  const dotsTouchProps = onTouchStart ? { onTouchStart, onTouchMove, onTouchEnd, className: 'flex items-center gap-2 cursor-grab active:cursor-grabbing' } : { className: 'flex items-center gap-2' };

  return (
    <div className="mt-4 flex flex-col items-center gap-4 sm:mt-6">
      {showDots && (
        <div className="flex min-h-[6px] items-center justify-center">
          <div {...dotsTouchProps}>
            {Array.from({ length: dotCount }, (_, i) => (
              <button
                key={i}
                onClick={() => onIndexChange(i)}
                aria-label={
                  currentIndex === i
                    ? `${ariaLabelPrefix} ${i + 1} of ${dotCount} (current)`
                    : `Go to ${ariaLabelPrefix.toLowerCase()} ${i + 1}`
                }
                className={`h-1.5 rounded-full transition-all duration-300 ease-out ${
                  currentIndex === i
                    ? 'w-6 bg-runcards-accent'
                    : 'w-1.5 bg-runcards-text-secondary/30 hover:bg-runcards-text-secondary/60'
                }`}
              />
            ))}
          </div>
        </div>
      )}
      {/* Mobile tap controls — on desktop the flanking side arrows handle navigation */}
      <div className="flex items-center gap-6 sm:hidden">
        <button
          type="button"
          onClick={goPrev}
          aria-label={`Previous ${ariaLabelPrefix.toLowerCase()}`}
          className={NAV_BUTTON_CLASS}
        >
          <ChevronLeft className="h-5 w-5 transition-transform duration-300 ease-out group-hover:-translate-x-0.5" />
        </button>
        <button
          type="button"
          onClick={goNext}
          aria-label={`Next ${ariaLabelPrefix.toLowerCase()}`}
          className={NAV_BUTTON_CLASS}
        >
          <ChevronRight className="h-5 w-5 transition-transform duration-300 ease-out group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

export default CarouselNav;
